// Typed-route href resolver for *union*-typed app routes.
//
// SvelteKit's `resolve()` (`$app/paths`) is typed with a per-route overload
// set: each concrete route id is its own call signature. A value typed as a
// *union* of route ids — e.g. a nav-link list's `href: Pathname` — is therefore
// rejected at the call site even though every member of the union is a valid
// argument, because a union is not assignable to any single overload.
//
// This wrapper funnels such `Pathname`-typed hrefs through one sound site:
// callers keep their typed `Pathname` value (no scattered `as` casts), and the
// single internal assertion below side-steps the overload-vs-union mismatch.
// It is for param-free static routes (the only kind passed as nav hrefs).

import { resolve } from '$app/paths';
import type { PathnameWithSearchOrHash, ResolvedPathname } from '$app/types';

/**
 * Resolve a route href (possibly a union of route ids, optionally carrying a
 * search string or hash — e.g. the deep-link `/login?return=…`) to a URL string.
 */
export function resolveHref(route: PathnameWithSearchOrHash): ResolvedPathname {
  // `resolve`'s per-route overload set cannot accept a union argument, so we
  // view it through a single union-accepting signature. Sound for the param-
  // free static routes passed as nav hrefs (the runtime value is a real id).
  return (resolve as (route: PathnameWithSearchOrHash) => ResolvedPathname)(route);
}
